import { useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { SupportAdminTicketResponse } from '@/lib/api';

export interface AdminTicketMessage {
  messageId: string;
  senderId: string | null;
  senderType: string;
  content: string;
  isInternal: boolean;
  deletedAt: string | null;
  createdAt: string;
}

interface UseAdminTicketReturn {
  ticket: SupportAdminTicketResponse | null;
  messages: AdminTicketMessage[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  addMessage: (content: string, isInternal?: boolean) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
}

export function useAdminTicket(ticketId: string | undefined): UseAdminTicketReturn {
  const queryClient = useQueryClient();

  const ticketQuery = useQuery({
    queryKey: queryKeys.customerSupport.adminTicketDetail(ticketId ?? ''),
    queryFn: () => api.getSupportTicketAdmin(ticketId!),
    enabled: !!ticketId,
  });

  const messagesQuery = useQuery({
    queryKey: queryKeys.customerSupport.adminTicketMessages(ticketId ?? ''),
    queryFn: () => api.listSupportTicketMessagesAdmin(ticketId!),
    enabled: !!ticketId,
  });

  // Fetching the ticket detail marks it read server-side (admin.ts ->
  // markSupportTicketRead). Refresh the derived badges so the worktop alert and
  // the per-tab unread counts clear immediately instead of lagging until the
  // next poll. Keyed on dataUpdatedAt so it fires on the initial load AND each
  // refetch. The unread item is gone from the counts only after a SUCCESSFUL
  // fetch, so we gate on isSuccess.
  const ticketFetchedAt = ticketQuery.dataUpdatedAt;
  const ticketFetchSucceeded = ticketQuery.isSuccess;
  useEffect(() => {
    if (!ticketId || !ticketFetchSucceeded) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications.summary() });
    queryClient.invalidateQueries({ queryKey: queryKeys.customerSupport.adminUnreadCounts() });
  }, [ticketId, ticketFetchSucceeded, ticketFetchedAt, queryClient]);

  const addMessageMutation = useMutation({
    mutationFn: ({ content, isInternal }: { content: string; isInternal: boolean }) =>
      api.createSupportTicketMessageAdmin(ticketId!, { content, isInternal }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customerSupport.adminTicketMessages(ticketId!),
      });
    },
  });

  const deleteMessageMutation = useMutation({
    mutationFn: (messageId: string) => api.deleteSupportMessageAdmin(ticketId!, messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customerSupport.adminTicketMessages(ticketId!),
      });
    },
  });

  const loading = ticketQuery.isLoading || messagesQuery.isLoading;
  const error = ticketQuery.error?.message ?? messagesQuery.error?.message ?? null;

  return {
    ticket: ticketQuery.data ?? null,
    messages: messagesQuery.data?.messages ?? [],
    loading,
    error,
    refresh: () => {
      ticketQuery.refetch();
      messagesQuery.refetch();
    },
    addMessage: async (content: string, isInternal: boolean = false) => {
      await addMessageMutation.mutateAsync({ content, isInternal });
    },
    deleteMessage: async (messageId: string) => {
      await deleteMessageMutation.mutateAsync(messageId);
    },
  };
}
